<template>
  <span class="cms-body" :class="{ loading: !contentIsLoaded }">
    <section class="cms-section" :key="sectionIndex" v-for="(section, sectionIndex) of sections">
      <div class="container maxWidth-sm">
        <template v-for="(slice, index) of section.slices">
          <!-- Content -->

          <CmsTextRenderer
            v-if="slice.slice_type === 'cms-text-block'"
            :key="index"
            class="cms-text-block-inner"
            :class="slice.slice_label"
            :html="cms.asHtml(slice.primary.content)"
          ></CmsTextRenderer>
          <CollapsedSection
            :key="`collapsed-section-${index}`"
            v-else-if="slice.slice_type === 'cms-collapsed-section'"
            :showDoneButton="!!slice.primary.show_done_button"
          >
            <template #text-above>
              <div v-html="cms.asHtml(slice.primary.text_above)"></div>
            </template>
            <template #text-below>
              <div v-html="cms.asHtml(slice.primary.text_below)"></div>
            </template>
          </CollapsedSection>

          <hr
            v-else-if="slice.slice_type === 'cms-horizontal-rule'"
            :key="`horizontal-${index}`"
            :class="[
              'hr-magazine-' + slice.primary.thickness,
              'hr-magazine-' + slice.primary.align,
            ]"
          />

          <CmsExcerpt
            v-else-if="slice.slice_type === 'cms-text-excerpt'"
            :key="`text-excerpt-${index}`"
            :slice="slice"
          />

          <CmsQuote
            v-else-if="slice.slice_type === 'cms-quote-embed'"
            :key="`quote-embed-${index}`"
            :slice="slice"
            :renderWaiter="renderWaiter"
          />

          <!-- Activities -->

          <ListenActivity
            :activityId="activityId(slice)"
            :key="cms.asText(slice.primary.caption_text)"
            :mandatory="isMandatoryActivity(slice)"
            :complete="isActivityComplete(slice)"
            v-else-if="slice.slice_type === 'cms-audio-player'"
            :cms-audio-src="slice.primary.clip.url"
            :track-title="cms.asText(slice.primary.caption_text)"
            album-title="Gyre"
            :activityIds="''"
            @activity-completed="handleActivityCompleted"
          >
            <span v-html="cms.asHtml(slice.primary.caption_title)" />
            <span v-html="cms.asHtml(slice.primary.caption_text)" />
            <span
              class="missing-audio-error"
              v-if="!slice.primary.clip.url"
              style="color: red; display: none"
              >MISSING AUDIO FILE</span
            >
          </ListenActivity>

          <LibraryItemLink
            :key="slice.primary.link.uid"
            :dayOrPageType="(slice.primary.link.data || {}).day_or_page_type"
            :activityId="activityId(slice)"
            :mandatory="isMandatoryActivity(slice)"
            :complete="isActivityComplete(slice)"
            :pageId="slice.primary.link.id"
            :pageUid="slice.primary.link.uid"
            v-else-if="slice.slice_type === 'cms-library-item-link'"
          >
            <span v-html="cms.asHtml(slice.primary.caption_title)" />
            <span v-html="cms.asHtml(slice.primary.caption_text)" />
            <span v-if="!slice.primary.link.id" style="color: red">MISSING LINK</span>
          </LibraryItemLink>

          <SelfReviewActivity
            :key="`self-review-${index}`"
            :activityId="activityId(slice)"
            :mandatory="isMandatoryActivity(slice)"
            :complete="isActivityComplete(slice)"
            :pageId="pageId"
            :pageUid="pageUid"
            v-else-if="
              slice.slice_type === 'cms-self-review-activity' && cmsPage.type === 'cms-develop-page'
            "
            :develop-page-doc="cmsPage"
            @self-review-completed="handleSelfReviewCompleted"
          >
            <span v-html="cms.asHtml(slice.primary.caption_title)" />
            <span
              v-if="cms.asText(slice.primary.caption_text)"
              v-html="cms.asHtml(slice.primary.caption_text)"
            />
          </SelfReviewActivity>

          <CmsVideoPlayer
            :key="`video-player-${index}`"
            v-else-if="slice.slice_type === 'cms-video-player'"
            :mediaSource="slice.primary.media_link.url"
          />

          <ol
            :key="`report-list-${index}`"
            v-else-if="slice.slice_type === 'cms-report-list'"
            class="infoNumberList sevenThingsToKnow"
          >
            <li v-for="(item, li) in slice.items" :key="li" :class="'li' + (li + 1)">
              <div class="infoNumber">{{ li + 1 }}</div>
              <div v-html="cms.asHtml(item.report_list_text)" />
            </li>
          </ol>

          <div :key="`report-visual-${index}`" v-else-if="slice.slice_type === 'cms-report-visual'">
            <TeamEffectivenessHeadsBreakdown
              v-if="slice.primary.report_visual_type === 'TeamEffectivenessBreakdown'"
              :reviewId="context['review'].id"
            />

            <TeamEffectivenessHeadWithInfo
              v-else-if="slice.primary.report_visual_type.includes('Info')"
              :reviewId="context['review'].id"
            />

            <HeadFromModel
              v-else-if="
                slice.primary.report_visual_type !== 'GroupCompositeHead' &&
                (slice.primary.report_visual_type.includes('Head') ||
                  slice.primary.report_visual_type.includes('Diamond'))
              "
              :headId="context['review'].id"
              :kind="slice.primary.report_visual_type"
              style="max-width: 40%; margin: 0 auto"
            />

            <TeamEffectivenessBarGraph
              :reviewId="context['review'].id"
              v-else-if="slice.primary.report_visual_type === 'TeamEffectivenessBarGraph'"
            />

            <CustomSoloBarGraph
              v-else-if="slice.primary.report_visual_type === 'CustomSoloBarGraph'"
            />

            <CustomViewpointsBarGraph
              v-else-if="slice.primary.report_visual_type === 'CustomViewpointsBarGraph'"
            />

            <CherishPieChartReportVisual
              :reviewId="context['review'].id"
              v-else-if="slice.primary.report_visual_type === 'TeamEffectivenessCherishPieChart'"
            />

            <CherishBarChartReportVisual
              :reviewId="context['review'].id"
              v-else-if="slice.primary.report_visual_type === 'TeamEffectivenessCherishBarChart'"
            />

            <ChangePieChartReportVisual
              :reviewId="context['review'].id"
              v-else-if="slice.primary.report_visual_type === 'TeamEffectivenessChangePieChart'"
            />

            <ChangeBarChartReportVisual
              :reviewId="context['review'].id"
              v-else-if="slice.primary.report_visual_type === 'TeamEffectivenessChangeBarChart'"
            />

            <CompositeAdhocGroupHead
              v-else-if="slice.primary.report_visual_type === 'GroupCompositeHead'"
            />
          </div>
        </template>
      </div>
    </section>
  </span>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

// eslint-disable-next-line import/no-unresolved
import { Dictionary } from "vue-router/types/router";
import { debug as d } from "debug";
import RenderWaiter from "../../services/utilities/render-waiter";

import HeadFromModel from "../head/head-from-model.vue";
import TeamEffectivenessHeadWithInfo from "../head/team-effectiveness-head-with-info.vue";
import TeamEffectivenessHeadsBreakdown from "../head/team-effectiveness-heads-breakdown.vue";
import TeamEffectivenessBarGraph from "../team-effectiveness/team-effectiveness-bar-graph.vue";
import CherishPieChartReportVisual from "../team-effectiveness/cherish-pie-chart-report-visual.vue";
import CherishBarChartReportVisual from "../team-effectiveness/cherish-bar-chart-report-visual.vue";
import ChangePieChartReportVisual from "../team-effectiveness/change-pie-chart-report-visual.vue";
import ChangeBarChartReportVisual from "../team-effectiveness/change-bar-chart-report-visual.vue";
import CustomSoloBarGraph from "../custom-reports/custom-solo-bar-graph.vue";
import CustomViewpointsBarGraph from "../custom-reports/custom-viewpoints-bar-graph.vue";

// eslint-disable-next-line import/extensions, import/no-unresolved
import ListenActivity from "./listen-activity.vue";
import CollapsedSection from "./collapsed-section.vue";
import LibraryItemLink from "./library-item-link.vue";
import SelfReviewActivity from "./self-review-activity.vue";
import {
  CmsDevelopPage,
  CmsSlice,
  isMandatoryActivity,
  sliceToActivityId,
} from "../../services/cms/cms-type-definitions";
import cms from "../../services/cms/cms";
import CmsExcerpt from "./cms-excerpt.vue";
import CmsQuote from "./cms-quote.vue";
import CmsVideoPlayer from "./cms-video-player.vue";
import CompositeAdhocGroupHead from "../head/composite-adhoc-group-head.vue";
import CmsTextRenderer from "./cms-text-renderer.vue";

const debug = d("cs.CmsBodyComponent");

const props = withDefaults(
  defineProps<{
    cmsPage: CmsDevelopPage;
    context?: Dictionary<string>;
    completedActivityIds?: string[];
  }>(),
  {
    context: undefined,
    completedActivityIds: () => [],
  },
);

const emit = defineEmits(["activity-completed", "self-review-completed"]);

const renderWaits = ref([]);
const renderWaiter = new RenderWaiter(renderWaits.value);

const sections = computed(() => {
  let section = {
    id: "",
    slices: [] as CmsSlice[],
  };
  const sectionsComputed = [section];

  props.cmsPage.data.body.forEach((slice) => {
    if (slice.slice_type !== "cms-section") {
      section.slices.push(slice);
    } else {
      if (!(sectionsComputed.length === 1 && section.slices.length === 0)) {
        section = { id: "", slices: [] };
        sectionsComputed.push(section);
      }
      section.id = slice.primary.id ?? "";
    }
  });

  return sectionsComputed;
});

const pageId = computed(() => props.cmsPage.id);

const pageUid = computed(() => props.cmsPage.uid);

const contentIsLoaded = computed(() => {
  const newValue = !!props.cmsPage && renderWaits.value.length === 0;
  debug("contentIsLoaded: {newValue}");
  return newValue;
});

const numberOfWaits = computed(() => renderWaits.value.length);

const activityId = (slice: CmsSlice): string => {
  return sliceToActivityId(slice, pageId.value);
};

const isMandatoryActivityLocal = (slice: CmsSlice): boolean => {
  return isMandatoryActivity(slice);
};

const isActivityComplete = (slice: CmsSlice): boolean => {
  const id = activityId(slice);
  return props.completedActivityIds.includes(id);
};

const handleActivityCompleted = (completedActivityId: string): void => {
  emit("activity-completed", completedActivityId);
};

const handleSelfReviewCompleted = (completedActivityId: string, answers: number[]): void => {
  emit("self-review-completed", completedActivityId, answers);
};
const step = null;

const cmsInstance = cms;
</script>

<style lang="less">
.cms-body {
  &.loading {
    visibility: hidden;
  }

  .block-img {
    display: flex;
    justify-content: center;
  }

  .align-left {
    .block-img {
      justify-content: start;
    }
  }

  .block-img img {
    max-width: 100%;
  }

  // Display broken links as plain text, and prevent clicking on them
  a[href=""] {
    color: inherit;
    text-decoration: inherit;
    font-weight: inherit;
    pointer-events: none;
  }
}

// On both web and mobile, everything was encoded with the intention of being
// without sections. To add them back in and keep the same navbars, we need to make it so
// cms bodies first and last sections have 0 padding.
.cms-body.cms-body.cms-body section {
  &:first-child {
    padding-top: 0px;
  }

  &:last-child {
    padding-bottom: 0px;
  }
}
</style>
